import { useEffect } from 'react';
import Layout from '../../../../components/layout/Layout';
import Breadcrumb from '../../../../components/layout/Breadcrumb';
import AboutSideNav from '../../../../components/about/AboutSideNav';

// ===== Association Overview (EN mirror) — full rebuild, Implementer A scope =====
// Design language mirrors JA overview (navy #1a2942 / brass #a38e65 / BTN-08 / navy panels /
//   gold accent headings / editorial rules / vertical timeline). Facts sourced from the same
//   canon as the JA page (mng-thebutler/00_business-overview, about/history, about/access). No invented
//   figures. EN pages must contain no Japanese characters (SEO rule 1-3).

export default function AboutOverviewPageEN() {
  useEffect(() => {
    document.title = 'Association Overview | Japan Butler Association';

    const metaDescription = document.querySelector('meta[name="description"]') || document.createElement('meta');
    metaDescription.setAttribute('name', 'description');
    metaDescription.setAttribute('content', 'Discover the background and organizational overview of the Japan Butler Association. Since 1890, we have promoted butler culture through professional training, certification, and international networks.');
    if (!document.querySelector('meta[name="description"]')) {
      document.head.appendChild(metaDescription);
    }

    const canonicalLink = document.querySelector('link[rel="canonical"]') || document.createElement('link');
    canonicalLink.setAttribute('rel', 'canonical');
    canonicalLink.setAttribute('href', 'https://butler-association.jp/en/about/overview');
    if (!document.querySelector('link[rel="canonical"]')) {
      document.head.appendChild(canonicalLink);
    }

    const hreflangJa = document.querySelector('link[rel="alternate"][hreflang="ja"]') || document.createElement('link');
    hreflangJa.setAttribute('rel', 'alternate');
    hreflangJa.setAttribute('hreflang', 'ja');
    hreflangJa.setAttribute('href', 'https://butler-association.jp/about/overview');
    if (!document.querySelector('link[rel="alternate"][hreflang="ja"]')) {
      document.head.appendChild(hreflangJa);
    }

    const hreflangEn = document.querySelector('link[rel="alternate"][hreflang="en"]') || document.createElement('link');
    hreflangEn.setAttribute('rel', 'alternate');
    hreflangEn.setAttribute('hreflang', 'en');
    hreflangEn.setAttribute('href', 'https://butler-association.jp/en/about/overview');
    if (!document.querySelector('link[rel="alternate"][hreflang="en"]')) {
      document.head.appendChild(hreflangEn);
    }

    const hreflangDefault = document.querySelector('link[rel="alternate"][hreflang="x-default"]') || document.createElement('link');
    hreflangDefault.setAttribute('rel', 'alternate');
    hreflangDefault.setAttribute('hreflang', 'x-default');
    hreflangDefault.setAttribute('href', 'https://butler-association.jp/about/overview');
    if (!document.querySelector('link[rel="alternate"][hreflang="x-default"]')) {
      document.head.appendChild(hreflangDefault);
    }

    document.documentElement.lang = 'en';
  }, []);

  const breadcrumbs = [
    { label: 'Home', href: '/en/' },
    { label: 'About Us', href: '/en/about' },
    { label: 'Overview' }
  ];

  const aboutNav = [
    { label: 'Overview', href: '/en/about/overview' },
    { label: 'Mission', href: '/en/about/mission' },
    { label: 'History', href: '/en/about/history' },
    { label: 'Organization', href: '/en/about/organization' },
    { label: 'Activities', href: '/en/about/activities' },
    { label: 'Access', href: '/en/about/access' }
  ];

  const BTN08_ON_LIGHT = "relative inline-flex items-center justify-center bg-[#1a2942] text-[#f8f6f0] border border-[#a38e65] px-[42px] py-[16px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#a38e65] hover:text-[#1a2942] hover:border-[#1a2942] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#a38e65]/45 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#1a2942]/45";
  const BTN08_ON_NAVY = "relative inline-flex items-center justify-center bg-[#a38e65] text-white font-semibold border border-[#1a2942] px-[42px] py-[16px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#1a2942] hover:text-[#f8f6f0] hover:border-[#a38e65] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#1a2942]/40 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#a38e65]/50";

  const corporateFacts = [
    { label: 'Name', value: 'Japan Butler Association' },
    { label: 'Founded', value: '1890 (Meiji 23)' },
    { label: 'Incorporated', value: 'February 19, 2025' },
    { label: 'Representative Director', value: 'Yuichi Ishii (Third President of the Association)' },
    { label: 'Location', value: '2-7-4 Aomi, Koto-ku, Tokyo 135-0064, Japan' },
    { label: 'Corp. No.', value: '2010005039584' },
    { label: 'Tel', value: '+81-3-6824-4086 (visits by appointment only)' },
    { label: 'Activities', value: 'Butler training / certification system / seminars & events / international exchange / publishing' },
    { label: 'Base', value: 'A headquarters and "Butler Salon" in Odaiba, recreating a room of a medieval European castle' },
  ];

  const businessFields = [
    { no: '01', en: 'Human Resources', ja: 'Training & Development', desc: 'Through systematic programs covering everything from fundamental skills to advanced techniques, we cultivate high-quality butlers, using the Odaiba Butler Salon as a place of hands-on practice.' },
    { no: '02', en: 'Certification', ja: 'Certification System', desc: 'A three-tier system — Associate, Professional, and Master Butler — plus specialist qualifications in dining, travel, and business manners, objectively certifying skill and ensuring service quality.' },
    { no: '03', en: 'Seminars', ja: 'Seminars & Events', desc: 'Practical seminars on leadership, hospitality, and communication for companies and individuals, bringing the conduct and mindset of the butler to the business world.' },
    { no: '04', en: 'International', ja: 'International Exchange', desc: 'Aiming to contribute to the global development of butler culture, we work to develop butlers who meet international standards and place importance on our readiness to serve guests from overseas.' },
    { no: '05', en: 'Publishing', ja: 'Publishing & Media', desc: 'Through specialist publications, books, and social channels including the "Butler Bell" YouTube channel, we convey the present state of butler culture to society.' },
  ];

  const historyDigest = [
    { year: '1890', label: 'Foundation', text: 'Amid the modernization following the Meiji Restoration, founder Shingo Tanaka introduced butler culture learned during his studies in Britain and founded the association.' },
    { year: '1903', label: 'Training begins', text: 'The first systematic butler training program opened, establishing a unique curriculum that fused Western service manners with Japanese etiquette.' },
    { year: '1955', label: 'The association divides', text: 'Differences over the direction of the association led it to divide into the Kanto and Kansai Butler Associations. For more than four decades the two trained butlers in their own manner, each building standards rooted in its region.' },
    { year: '2000', label: 'Reunification', text: 'Under the second President, Kazuhiko Yamada, the previously separate Kanto and Kansai associations reunified. The nationwide standards of quality and the training system in use today were born at this moment.' },
    { year: '2023', label: 'Third President', text: 'Yuichi Ishii (Butler Bell) became the third President under the banner "an age in which butlers are chosen," and the register of serving butlers was published in the same year.' },
    { year: '2024', label: 'Fees and enquiries opened', text: 'Fees were published and an enquiry channel opened for clients without an introduction, widening a door that had been open only to a few.' },
    { year: '2025', label: 'Incorporation', text: 'On February 19, 2025 the Japan Butler Association was incorporated as a general incorporated association, with Yuichi Ishii as Representative Director. The association also marked its 135th anniversary that year.' },
  ];

  return (
    <Layout>
      {/* Hero — full-width billboard (image + navy overlay + left text) */}
      <section className="relative bg-[#1a2942] overflow-hidden min-h-[520px] lg:min-h-[600px] flex items-center">
        <img
          src="/images/salon0926/DSC00335.jpg"
          alt="The dining room of the butler salon under evening lighting"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(16,20,26,0.92)_0%,rgba(16,20,26,0.72)_48%,rgba(16,20,26,0.35)_100%)]"></div>
        <div className="relative z-10 w-full max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10 pt-32 pb-16 lg:pt-36 lg:pb-20">
          <div className="max-w-[680px]">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.32em] text-xs sm:text-[13px] text-[#d7b779] mb-4">Association Overview</span>
            <div className="w-12 h-px bg-[#a38e65] mb-6"></div>
            <h1 className="jba-display font-normal text-white text-[clamp(28px,3.4vw,44px)] leading-[1.4] tracking-[0.04em] mb-6">
              <span className="block">135 Years of Nurturing</span>
              <span className="block">Butler Culture in Japan</span>
            </h1>
            <div className="space-y-5">
              <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-white/90 leading-[1.95]">
                Since its founding in 1890 (Meiji 23), the Japan Butler Association has been dedicated to promoting and developing butler culture in Japan.
              </p>
              <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-white/90 leading-[1.95]">
                By fusing the European butler tradition with the Japanese spirit of "Omotenashi," we cultivate professional butlers of world-class standard.
              </p>
            </div>
          </div>
        </div>
      </section>
      {/* パンくず: ビルボード直後に配置（Yuichi 2026-07-17 指摘②）。共通Breadcrumbのヘッダー逃げpaddingを子セレクタで通常paddingへ上書き */}
      <div className="bg-white [&>div]:!pt-6">
        <Breadcrumb items={breadcrumbs} />
      </div>

      {/* Body = side nav + content (2 columns) */}
      <div className="bg-white">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16 lg:py-20">
          <div className="grid lg:grid-cols-[248px_1fr] gap-8 lg:gap-14">

            <AboutSideNav items={aboutNav} activeHref="/en/about/overview" eyebrow="About Us" heading="About the Association" />

            <div className="min-w-0 space-y-16 sm:space-y-20">

              {/* S1. Lead */}
              <section>
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">I. Who We Are</span>
                <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
                  Japan's only <span className="text-[#a38e65]">dedicated butler institution</span>
                </h2>
                <div className="space-y-[18px] font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl">
                  <div className="space-y-5">
                    <p>
                      The Japan Butler Association is the country's only dedicated butler institution, with a lineage spanning more than 130 years.
                    </p>
                    <p>
                      Far beyond simple housekeeping, our professionals combine the discipline and dignity of the British style with Japan's refined etiquette to support clients comprehensively, from private life to business.
                    </p>
                  </div>
                  <div className="space-y-5">
                    <p>
                      What we provide is not a substitute for labor, but an environment itself — one freed from arrangements and chores, allowing clients to focus on what truly matters: work, family, and rest.
                    </p>
                    <p>
                      The butler enters the most intimate part of the household, quietly maintaining the foundations of daily life as the "heart of the home."
                    </p>
                  </div>
                  <div className="space-y-5">
                    <p>
                      Our headquarters and "Butler Salon" in Odaiba, recreating a room of a medieval European castle, is a real, physical base for education and experience.
                    </p>
                    <p>
                      This tangible assurance of service quality sets us apart from businesses that operate online alone.
                    </p>
                  </div>
                </div>
                <dl className="grid grid-cols-2 md:grid-cols-4 gap-y-6 gap-x-4 mt-10 border-t border-[#e2ddd2] pt-8">
                  <div>
                    <dt className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-1.5">Founded</dt>
                    <dd className="font-['Playfair_Display',serif] text-2xl text-[#1a2942]">1890</dd>
                  </div>
                  <div>
                    <dt className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-1.5">Heritage</dt>
                    <dd className="font-['Playfair_Display',serif] text-2xl text-[#1a2942]">135<span className="text-sm text-[#8f7a56] ml-1">yrs</span></dd>
                  </div>
                  <div>
                    <dt className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-1.5">Presidents</dt>
                    <dd className="font-['Playfair_Display',serif] text-2xl text-[#1a2942]">3</dd>
                  </div>
                  <div>
                    <dt className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-1.5">Base</dt>
                    <dd className="font-['Noto_Serif_JP',serif] text-lg text-[#1a2942] leading-tight pt-1">Odaiba, Tokyo</dd>
                  </div>
                </dl>
              </section>

              {/* S2. Corporate Profile — definition list (new variation ①) */}
              <section>
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">II. Corporate Profile</span>
                <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
                  Corporate Profile
                </h2>
                <div className="grid lg:grid-cols-[1fr_320px] gap-8 lg:gap-12 items-start">
                  <dl className="border-t-2 border-[#1a2942]">
                    {corporateFacts.map((row) => (
                      <div key={row.label} className="flex flex-col sm:flex-row gap-1 sm:gap-6 border-b border-[#e2ddd2] py-4">
                        <dt className="sm:w-[124px] sm:shrink-0 font-['Noto_Serif_JP',serif] text-sm text-[#8f7a56] tracking-[0.06em] pt-0.5">{row.label}</dt>
                        <dd className="flex-1 font-['Noto_Serif_JP',serif] text-[15px] sm:text-base text-[#1a2942] leading-[1.85]">{row.value}</dd>
                      </div>
                    ))}
                  </dl>
                  <div className="lg:pt-1">
                    <img
                      src="/images/salon0926/20260912_093331.jpg"
                      alt="The sofa corner beside the fireplace in the butler salon"
                      className="w-full h-56 lg:h-[320px] object-cover border border-[#a38e65]/30"
                    />
                  </div>
                </div>
              </section>

              {/* S3. Our Purpose — quotation feature (new variation ②) */}
              <section>
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">III. Our Purpose</span>
                <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
                  What We Hold Dear
                </h2>
                <div className="bg-[#1a2942] px-7 sm:px-12 lg:px-16 py-12 sm:py-14 lg:py-16">
                  <div className="text-[#d7b779] font-['Playfair_Display',serif] text-5xl leading-none mb-4">“</div>
                  <blockquote className="jba-display font-normal text-[#f8f6f0] text-xl sm:text-2xl lg:text-[30px] leading-[1.65] tracking-[0.02em] max-w-3xl">
                    The fusion of the Japanese spirit of hospitality with Western butler service. Sensing a need before the master gives it words, and moving as naturally as air — this "anticipatory service" is what we consider the essence of the butler.
                  </blockquote>
                  <div className="w-11 h-px bg-[#a38e65] mt-8 mb-10"></div>
                  <div className="grid sm:grid-cols-3 gap-8 lg:gap-10">
                    <div>
                      <div className="font-['Playfair_Display',serif] italic text-[#a38e65] text-sm tracking-[0.06em] mb-2">Value i.</div>
                      <h3 className="jba-display font-medium text-[#f8f6f0] text-lg mb-2.5">Time & Peace of Mind</h3>
                      <p className="font-['Noto_Serif_JP',serif] text-sm text-[#ded8cc] leading-[1.9]">Freed from chores, clients can focus on work, family, and rest — we deliver that environment itself.</p>
                    </div>
                    <div>
                      <div className="font-['Playfair_Display',serif] italic text-[#a38e65] text-sm tracking-[0.06em] mb-2">Value ii.</div>
                      <h3 className="jba-display font-medium text-[#f8f6f0] text-lg mb-2.5">Heart of the Home</h3>
                      <p className="font-['Noto_Serif_JP',serif] text-sm text-[#ded8cc] leading-[1.9]">Entering the innermost part of daily life, quietly ordering its foundations and attending to a family's nuances.</p>
                    </div>
                    <div>
                      <div className="font-['Playfair_Display',serif] italic text-[#a38e65] text-sm tracking-[0.06em] mb-2">Value iii.</div>
                      <h3 className="jba-display font-medium text-[#f8f6f0] text-lg mb-2.5">Unseen Service</h3>
                      <p className="font-['Noto_Serif_JP',serif] text-sm text-[#ded8cc] leading-[1.9]">Support that is sure yet never conspicuous — we pursue anticipatory hospitality.</p>
                    </div>
                  </div>
                </div>
              </section>

              {/* S4. Business Fields — numbered two-column blocks */}
              <section>
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">IV. What We Do</span>
                <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-3">
                  Our <span className="text-[#a38e65]">Five Fields</span>
                </h2>
                <div className="space-y-5 mb-9">
                  <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl">
                    We advance the spread and growth of butler culture through five pillars: developing people, certifying skill, widening learning, connecting with the world, and telling the story in words.
                  </p>
                  <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl">
                    Each works together to deliver high-quality butler service to society.
                  </p>
                </div>
                <div className="grid md:grid-cols-2 gap-x-10 gap-y-8">
                  {businessFields.map((f) => (
                    <div key={f.no} className="flex gap-5 border-t border-[#e2ddd2] pt-6">
                      <span className="font-['Playfair_Display',serif] text-[#d7b779] text-3xl leading-none shrink-0 pt-1">{f.no}</span>
                      <div>
                        <span className="block font-['Playfair_Display',serif] italic text-[#8f7a56] text-[13px] tracking-[0.06em] mb-1">{f.en}</span>
                        <h3 className="jba-display font-medium text-[#1a2942] text-lg sm:text-xl mb-2.5 leading-[1.5]">{f.ja}</h3>
                        <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">{f.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {/* S5. Heritage — vertical timeline (new variation ③) */}
              <section>
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">V. Heritage</span>
                <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
                  Milestones at a Glance
                </h2>
                <div className="grid lg:grid-cols-[1fr_300px] gap-8 lg:gap-12 items-start">
                  <ol className="relative border-l border-[#e2ddd2] ml-1">
                    {historyDigest.map((h) => (
                      <li key={h.year} className="relative pl-7 sm:pl-9 pb-8 last:pb-0">
                        <span className="absolute -left-[6px] top-1.5 w-[11px] h-[11px] rounded-full bg-[#a38e65] ring-4 ring-white"></span>
                        <div className="flex items-baseline gap-3 mb-1.5">
                          <span className="font-['Playfair_Display',serif] text-xl text-[#1a2942]">{h.year}</span>
                          <span className="jba-display font-medium text-[#8f7a56] text-[15px] sm:text-base">{h.label}</span>
                        </div>
                        <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">{h.text}</p>
                      </li>
                    ))}
                  </ol>
                  <div className="lg:pt-1">
                    <img
                      src="/images/generated/jba-still-heritage-01.webp"
                      alt="Furnishings that tell the story of the association's journey"
                      className="w-full h-56 lg:h-[300px] object-cover border border-[#a38e65]/30 mb-5"
                    />
                    <a href="/en/about/history" className={`${BTN08_ON_LIGHT} w-full`}>See the Full History</a>
                  </div>
                </div>
              </section>

              {/* S6. Next Steps — navy CTA panel */}
              <section>
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">VI. Next Steps</span>
                <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
                  Learn More About Us
                </h2>
                <div className="bg-[#1a2942] border border-[#a38e65]/45 p-8 sm:p-10 lg:p-12">
                  <div className="space-y-5 mb-8">
                    <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-[#e7e2d8] leading-[1.95] max-w-3xl">
                      The Japan Butler Association roots butler culture in society through butler training and certification, practical seminars, and the operation of the butler staffing service "THE BUTLER."
                    </p>
                    <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-[#e7e2d8] leading-[1.95] max-w-3xl">
                      If you would like to know more, please explore our certifications and services.
                    </p>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
                    <a href="/en/qualifications" className={BTN08_ON_NAVY}>Certifications</a>
                    <a href="/en/butler-services/" className={BTN08_ON_NAVY}>Butler Services</a>
                    <a href="/en/contact" className={BTN08_ON_NAVY}>Contact Us</a>
                  </div>
                </div>
              </section>

            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
